import React, { useEffect, useState } from "react";
import {
  formatPhoneNumberForSms,
  formatPhoneNumber,
} from "../../utils/phoneNumberUtils";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faUsers,
  faCarSide,
  faCheck,
  faLocationDot,
  faPhone,
  faClock,
  faCircleCheck,
} from "@fortawesome/free-solid-svg-icons";

const PassengerDetail = ({
  passenger,
  index,
  openTimeModal,
  quickSetStatus,
  confirmedPickupAssignment,
}) => {
  const pickupId = passenger.id || passenger._id;
  const [pendingStatus, setPendingStatus] = useState(null);
  const [countdown, setCountdown] = useState(5);
  const [sendingStatus, setSendingStatus] = useState(false);

  const statusActions = [
    {
      label: "Pending",
      value: null,
      icon: faClock,
      activeClass: "bg-[#004a7e] text-white",
      idleClass: "bg-[#eaf3fa] text-[#004a7e] hover:bg-[#d8eaf6]",
    },
    {
      label: "On way",
      value: "On-Route",
      icon: faCarSide,
      activeClass: "bg-[#b7791f] text-white",
      idleClass: "bg-yellow-100 text-yellow-800 hover:bg-yellow-200",
    },
    {
      label: "Arrived",
      value: "Arrived",
      icon: faCheck,
      activeClass: "bg-success text-white",
      idleClass: "bg-green-100 text-green-800 hover:bg-green-200",
    },
  ];

  const circleClass =
    passenger.pickupStatus === "Arrived"
      ? "bg-success text-white"
      : passenger.pickupStatus === "On-Route"
        ? "bg-warning text-white"
        : "bg-[#eaf3fa] text-[#004a7e]";

  useEffect(() => {
    if (!pendingStatus) return undefined;

    setCountdown(5);

    const countdownInterval = setInterval(() => {
      setCountdown((seconds) => Math.max(seconds - 1, 0));
    }, 1000);

    const sendTimeout = setTimeout(async () => {
      setSendingStatus(true);
      try {
        await quickSetStatus(pendingStatus.pickupId, pendingStatus.status);
      } finally {
        setSendingStatus(false);
        setPendingStatus(null);
      }
    }, 5000);

    return () => {
      clearInterval(countdownInterval);
      clearTimeout(sendTimeout);
    };
  }, [pendingStatus, quickSetStatus]);

  const queueStatusUpdate = (action) => {
    if (!action.value || sendingStatus) return;

    setPendingStatus({
      pickupId,
      status: action.value,
      label: action.value === "On-Route" ? "On the Way" : action.label,
      passengerName: passenger.name,
    });
  };

  return (
    <li
      key={pickupId}
      data-tour={index === 1 ? "pickup-card" : undefined}
      className="relative px-4 sm:px-5 py-4 list-none hover:bg-gray-50 transition-colors"
    >
      <div className="flex items-start gap-3">
        {/* Numbered status circle */}
        <div
          className={`flex-shrink-0 w-10 h-10 rounded-full flex items-center justify-center font-bold text-sm ${circleClass}`}
        >
          {passenger.pickupStatus === "Arrived" ? (
            <FontAwesomeIcon icon={faCircleCheck} />
          ) : (
            (index ?? "•")
          )}
        </div>

        {/* Main content */}
        <div className="flex-grow min-w-0">
          {/* Name + pickup time */}
          <div className="flex items-start justify-between gap-2">
            <div className="flex items-center gap-2 flex-wrap min-w-0">
              <h3 className="font-semibold text-gray-900 leading-tight">
                {passenger.name}
              </h3>
              {(passenger.noOfPpl || 1) > 1 && (
                <div className="tooltip" data-tip="No. of People">
                  <span className="inline-flex items-center gap-1 bg-[#004a7e] text-white py-0.5 px-2 rounded-full text-[11px] font-semibold shadow-sm">
                    <FontAwesomeIcon icon={faUsers} className="text-[10px]" />
                    {passenger.noOfPpl}
                  </span>
                </div>
              )}
            </div>
            <button
              data-tour={index === 1 ? "pickup-time" : undefined}
              type="button"
              disabled={!confirmedPickupAssignment}
              onClick={() => openTimeModal(pickupId)}
              className="inline-flex flex-shrink-0 items-center gap-1 bg-[#004a7e] text-white text-xs font-semibold px-2.5 py-1 rounded-full shadow-sm hover:bg-[#003a64] disabled:opacity-50"
              title={
                passenger.pickupTime
                  ? "Edit pickup time and notify passenger"
                  : "Set pickup time and notify passenger"
              }
            >
              <FontAwesomeIcon icon={faClock} className="text-[10px]" />
              {passenger.pickupTime || "Set time"}
            </button>
          </div>

          {/* Phone */}
          <div className="mt-1">
            <a
              href={`tel:${formatPhoneNumberForSms(passenger.phone)}`}
              aria-label={`Call ${passenger.name}`}
              className="mr-2 inline-flex h-7 w-7 items-center justify-center rounded-full bg-green-100 text-green-700 hover:bg-green-200"
            >
              <FontAwesomeIcon icon={faPhone} className="text-xs" />
            </a>
            <a
              href={`sms:${formatPhoneNumberForSms(passenger.phone)}`}
              className="inline-flex items-center gap-1.5 text-sm text-[#004a7e] font-medium hover:underline"
            >
              {formatPhoneNumber(passenger.phone)}
            </a>
          </div>

          {/* Address */}
          <div className="mt-1 flex items-start gap-1.5 text-sm text-gray-600">
            <FontAwesomeIcon
              icon={faLocationDot}
              className="text-xs mt-1 text-gray-400 flex-shrink-0"
            />
            <a
              href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(
                passenger.rideLocation,
              )}`}
              target="_blank"
              rel="noopener noreferrer"
              className="hover:underline break-words"
            >
              {passenger.rideLocation}
            </a>
          </div>

          {/* Status step buttons */}
          <div className="mt-4">
            <div
              data-tour={index === 1 ? "status-steps" : undefined}
              className="flex w-full overflow-hidden rounded-xl bg-gray-100 p-1"
            >
              {statusActions.map((action, actionIndex) => {
                const isActive =
                  action.value === passenger.pickupStatus ||
                  (!passenger.pickupStatus && action.value === null);
                const canUpdate =
                  confirmedPickupAssignment &&
                  action.value &&
                  !isActive &&
                  !pendingStatus &&
                  !sendingStatus;
                const clipPath =
                  actionIndex === 0
                    ? "polygon(0 0, calc(100% - 12px) 0, 100% 50%, calc(100% - 12px) 100%, 0 100%)"
                    : actionIndex === statusActions.length - 1
                      ? "polygon(0 0, 100% 0, 100% 100%, 0 100%, 12px 50%)"
                      : "polygon(0 0, calc(100% - 12px) 0, 100% 50%, calc(100% - 12px) 100%, 0 100%, 12px 50%)";

                return (
                  <button
                    key={action.label}
                    type="button"
                    disabled={
                      !confirmedPickupAssignment ||
                      Boolean(pendingStatus) ||
                      sendingStatus
                    }
                    onClick={() => canUpdate && queueStatusUpdate(action)}
                    className={`relative flex-1 min-w-0 py-2 pl-4 pr-5 text-xs font-bold transition-all ${
                      actionIndex > 0 ? "-ml-2" : ""
                    } ${isActive ? action.activeClass : action.idleClass} ${
                      canUpdate ? "cursor-pointer" : "cursor-default"
                    } ${
                      isActive
                        ? "z-10 scale-[1.03] shadow-lg ring-2 ring-[#004a7e]"
                        : "opacity-95"
                    } disabled:opacity-50`}
                    style={{ clipPath }}
                    title={
                      action.value
                        ? `Notify ${passenger.name}: ${action.label}`
                        : "Pending pickup update"
                    }
                  >
                    <span className="inline-flex items-center justify-center gap-1 whitespace-nowrap">
                      <FontAwesomeIcon icon={action.icon} />
                      {action.label}
                    </span>
                  </button>
                );
              })}
            </div>
            {!confirmedPickupAssignment && (
              <p className="mt-2 text-xs text-amber-700">
                Acknowledge assignments before sending pickup updates.
              </p>
            )}
          </div>
        </div>
      </div>

      {pendingStatus && (
        <div className="fixed inset-x-3 bottom-4 z-50 sm:left-1/2 sm:right-auto sm:w-full sm:max-w-md sm:-translate-x-1/2">
          <div className="rounded-2xl bg-gray-900 p-4 text-white shadow-2xl">
            <div className="flex items-start gap-3">
              <div className="mt-0.5 flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-full bg-white/10">
                <FontAwesomeIcon icon={faClock} />
              </div>
              <div className="min-w-0 flex-1">
                <p className="text-sm font-bold">
                  {sendingStatus
                    ? "Sending notification..."
                    : `Sending in ${countdown} seconds`}
                </p>
                <p className="mt-1 text-xs text-white/75">
                  {pendingStatus.label} update for {pendingStatus.passengerName}
                  . You can cancel before it sends.
                </p>
                <div className="mt-3 h-1.5 overflow-hidden rounded-full bg-white/15">
                  <div
                    className="h-full rounded-full bg-white transition-all duration-1000"
                    style={{ width: `${((5 - countdown) / 5) * 100}%` }}
                  />
                </div>
              </div>
              <button
                type="button"
                disabled={sendingStatus}
                onClick={() => setPendingStatus(null)}
                className="rounded-full bg-white px-3 py-1.5 text-xs font-bold text-gray-900 disabled:opacity-50"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </li>
  );
};

export default PassengerDetail;
